/**
 * userLearningPathStore — learner learning-path slice (Zustand).
 *
 *   GET  /api/learners/learning-paths
 *   GET  /api/learners/learning-paths/:id
 *   GET  /api/learners/learning-paths/:id/progress
 *   POST /api/learners/learning-paths/:id/enroll
 */
import { apiRequest, normalizeList } from "./userUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const userLearningPathInitialState = {
  userLearningPaths: [],
  userLearningPathsLoading: false,
  userLearningPathsError: null,
  userLearningPathsTotal: 0,
  userLearningPathsPages: 1,

  userLearningPath: null,
  userLearningPathLoading: false,
  userLearningPathError: null,

  userLearningPathProgress: null,
  userLearningPathProgressLoading: false,
  userLearningPathProgressError: null,

  userLearningPathEnrollment: null,
  userLearningPathEnrollmentLoading: false,
  userLearningPathEnrollmentError: null,
};

export function createUserLearningPathActions(set, get) {
  return {
    // GET /api/learners/learning-paths
    fetchUserLearningPaths: async (params = {}) => {
      set({ userLearningPathsLoading: true, userLearningPathsError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const response = await apiRequest(
          get,
          `/api/learners/learning-paths${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeList(response, [
          "learningPaths",
          "paths",
        ]);
        set({
          userLearningPathsLoading: false,
          userLearningPaths: items,
          userLearningPathsTotal: total,
          userLearningPathsPages: totalPages,
        });
        return { success: true, items, total, totalPages };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load learning paths");
        set({ userLearningPathsLoading: false, userLearningPathsError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/learning-paths/:id
    fetchUserLearningPath: async (id) => {
      set({ userLearningPathLoading: true, userLearningPathError: null });
      try {
        const response = await apiRequest(get, `/api/learners/learning-paths/${id}`);
        const path = response?.data ?? response?.learningPath ?? response?.path ?? response;
        set({ userLearningPathLoading: false, userLearningPath: path });
        return { success: true, data: path };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load learning path");
        set({ userLearningPathLoading: false, userLearningPathError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/learning-paths/:id/progress
    fetchUserLearningPathProgress: async (id) => {
      set({ userLearningPathProgressLoading: true, userLearningPathProgressError: null });
      try {
        const response = await apiRequest(
          get,
          `/api/learners/learning-paths/${id}/progress`
        );
        const progress = response?.data ?? response?.progress ?? response;
        set({ userLearningPathProgressLoading: false, userLearningPathProgress: progress });
        return { success: true, data: progress };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load learning path progress");
        set({ userLearningPathProgressLoading: false, userLearningPathProgressError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/learners/learning-paths/:id/enroll
    enrollUserLearningPath: async (id, body = {}) => {
      set({ userLearningPathEnrollmentLoading: true, userLearningPathEnrollmentError: null });
      try {
        const response = await apiRequest(
          get,
          `/api/learners/learning-paths/${id}/enroll`,
          { method: "POST", body }
        );
        const enrollment = response?.data ?? response;
        set({
          userLearningPathEnrollmentLoading: false,
          userLearningPathEnrollment: enrollment,
        });
        return { success: true, data: enrollment };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to enroll in learning path");
        set({ userLearningPathEnrollmentLoading: false, userLearningPathEnrollmentError: msg });
        return { success: false, error: msg };
      }
    },
  };
}
